import { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { useDarkMode } from '@/hooks/useDarkMode';
import useScrollLock from '@/hooks/useScrollLock';

import { logger } from '@/utils/logger';
import { Check, Copy, Eye, Globe, Lock, Pencil, Share2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
export default function WishlistShareModal({ wishlist, onClose, onUpdated, darkMode: darkModeProp }) {
  useScrollLock(true);
  const darkModeHook = useDarkMode();
  const darkMode = darkModeProp || darkModeHook;
  const [permission, setPermission] = useState(wishlist.share_permission || 'view');
  const [isShared, setIsShared] = useState(!!wishlist.is_shared);
  const [shareToken, setShareToken] = useState(wishlist.share_token || '');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  // Use REACT_APP_BACKEND_URL so the OG-rich preview URL works even when the
  // SPA and API are hosted on different origins. Falls back to current origin
  // when the env var is missing (e.g. local dev with same-origin proxy).
  const backendOrigin = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const shareUrl = shareToken
    ? `${backendOrigin}/api/share/wishlist/${shareToken}`
    : '';

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const enableShare = async (perm = permission) => {
    setBusy(true);
    try {
      const res = await api.post(`/wishlists/${wishlist.id}/share-collab`, { permission: perm });
      setShareToken(res.data.share_token);
      setIsShared(true);
      setPermission(res.data.permission);
      toast.success('Share link is ready!');
      if (onUpdated) onUpdated({ ...wishlist, is_shared: true, share_token: res.data.share_token, share_permission: res.data.permission });
      return true;
    } catch (err) {
      logger.error(err);
      toast.error('Failed to enable sharing');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const disableShare = async () => {
    setBusy(true);
    try {
      await api.post(`/wishlists/${wishlist.id}/unshare`);
      setIsShared(false);
      toast.success('Sharing disabled');
      if (onUpdated) onUpdated({ ...wishlist, is_shared: false });
    } catch (err) {
      logger.error(err);
      toast.error('Failed to disable sharing');
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Link copied!');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${wishlist.name} - Collaborative Wishlist`,
          text: `Help me build this wishlist on Mementa!`,
          url: shareUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') logger.error(err);
      }
    } else {
      handleCopy();
    }
  };

  const handlePermissionChange = async (newPerm) => {
    const previous = permission;
    setPermission(newPerm);
    if (isShared) {
      const ok = await enableShare(newPerm);
      if (!ok) setPermission(previous);
    }
  };

  return (
    <div
      data-testid="wishlist-share-modal"
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 overflow-y-auto"
      style={{ background: 'rgba(0, 0, 0, 0.85)', backdropFilter: 'blur(4px)', overscrollBehavior: 'contain' }}
      onClick={onClose}
    >
      <div
        className="rounded-2xl shadow-2xl w-full max-w-lg my-auto"
        style={{ background: darkMode?.isDark ? '#2D4354' : 'white' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 sm:p-8 relative">
          <button
            data-testid="wishlist-share-close-btn"
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-black/5"
            style={{ color: darkMode?.isDark ? '#FED7A5' : '#73766A' }}
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: wishlist.color, color: 'white' }}
            >
              <Share2 className="w-5 h-5" />
            </div>
            <div>
              <h2
                className="text-xl sm:text-2xl font-semibold"
                style={{ fontFamily: 'Playfair Display, serif', color: darkMode?.isDark ? '#FED7A5' : '#20212B' }}
              >
                Share "{wishlist.name}"
              </h2>
              <p className="text-xs" style={{ color: darkMode?.isDark ? '#E5E5E5' : '#73766A' }}>
                Invite friends & family to collaborate
              </p>
            </div>
          </div>

          {/* Status */}
          <div
            className="mt-6 mb-4 flex items-center justify-between p-4 rounded-xl"
            style={{
              background: isShared
                ? (darkMode?.isDark ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.08)')
                : (darkMode?.isDark ? 'rgba(255,255,255,0.05)' : '#F9FAFB'),
              border: `1px solid ${isShared ? 'rgba(16, 185, 129, 0.3)' : (darkMode?.isDark ? 'rgba(255,255,255,0.1)' : '#E5E7EB')}`
            }}
          >
            <div className="flex items-center gap-3">
              {isShared ? <Globe className="w-5 h-5" style={{ color: '#10B981' }} /> : <Lock className="w-5 h-5" style={{ color: '#73766A' }} />}
              <div>
                <p className="text-sm font-semibold" style={{ color: darkMode?.isDark ? '#FFFAF5' : '#20212B' }}>
                  {isShared ? 'Sharing is on' : 'Private'}
                </p>
                <p className="text-xs" style={{ color: darkMode?.isDark ? '#E5E5E5' : '#73766A' }}>
                  {isShared ? 'Anyone with the link can access' : 'Only you can see this wishlist'}
                </p>
              </div>
            </div>
            <Button
              data-testid={isShared ? 'wishlist-disable-share-btn' : 'wishlist-enable-share-btn'}
              onClick={isShared ? disableShare : () => enableShare(permission)}
              disabled={busy}
              size="sm"
              style={{
                background: isShared ? '#DC2626' : '#9E6752',
                color: 'white'
              }}
            >
              {busy ? '...' : (isShared ? 'Stop Sharing' : 'Enable Sharing')}
            </Button>
          </div>

          {/* Permission selector */}
          <div className="mb-4">
            <p className="text-sm font-semibold mb-2" style={{ color: darkMode?.isDark ? '#FED7A5' : '#20212B' }}>
              Who can do what
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="wishlist-permission-view"
                onClick={() => handlePermissionChange('view')}
                disabled={busy}
                className="p-3 rounded-lg text-left transition-all"
                style={{
                  border: `2px solid ${permission === 'view' ? '#9E6752' : (darkMode?.isDark ? 'rgba(255,255,255,0.1)' : '#E5E7EB')}`,
                  background: permission === 'view' ? (darkMode?.isDark ? 'rgba(158, 103, 82, 0.15)' : 'rgba(158, 103, 82, 0.08)') : 'transparent'
                }}
              >
                <Eye className="w-4 h-4 mb-1" style={{ color: '#9E6752' }} />
                <p className="text-sm font-medium" style={{ color: darkMode?.isDark ? '#FFFAF5' : '#20212B' }}>View only</p>
                <p className="text-xs" style={{ color: darkMode?.isDark ? '#E5E5E5' : '#73766A' }}>See but not edit</p>
              </button>
              <button
                data-testid="wishlist-permission-edit"
                onClick={() => handlePermissionChange('edit')}
                disabled={busy}
                className="p-3 rounded-lg text-left transition-all"
                style={{
                  border: `2px solid ${permission === 'edit' ? '#9E6752' : (darkMode?.isDark ? 'rgba(255,255,255,0.1)' : '#E5E7EB')}`,
                  background: permission === 'edit' ? (darkMode?.isDark ? 'rgba(158, 103, 82, 0.15)' : 'rgba(158, 103, 82, 0.08)') : 'transparent'
                }}
              >
                <Pencil className="w-4 h-4 mb-1" style={{ color: '#9E6752' }} />
                <p className="text-sm font-medium" style={{ color: darkMode?.isDark ? '#FFFAF5' : '#20212B' }}>Can add gifts</p>
                <p className="text-xs" style={{ color: darkMode?.isDark ? '#E5E5E5' : '#73766A' }}>Collaborators can add</p>
              </button>
            </div>
          </div>

          {/* Share link */}
          {isShared && shareUrl && (
            <div className="mb-2">
              <p className="text-sm font-semibold mb-2" style={{ color: darkMode?.isDark ? '#FED7A5' : '#20212B' }}>
                Share link
              </p>
              <div className="flex items-stretch gap-2">
                <input
                  data-testid="wishlist-share-link-input"
                  readOnly
                  value={shareUrl}
                  onClick={(e) => e.target.select()}
                  className="flex-1 p-2.5 text-sm rounded-lg border"
                  style={{
                    background: darkMode?.isDark ? 'rgba(255,255,255,0.05)' : '#F9FAFB',
                    color: darkMode?.isDark ? '#FED7A5' : '#20212B',
                    borderColor: darkMode?.isDark ? 'rgba(255,255,255,0.1)' : '#E5E7EB'
                  }}
                />
                <Button
                  data-testid="wishlist-copy-link-btn"
                  onClick={handleCopy}
                  size="icon"
                  style={{ background: copied ? '#10B981' : '#9E6752', color: 'white' }}
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
                <Button
                  data-testid="wishlist-native-share-btn"
                  onClick={handleNativeShare}
                  size="icon"
                  variant="outline"
                >
                  <Share2 className="w-4 h-4" />
                </Button>
              </div>

              {/* Link preview, shows how the link will appear when shared
                  on social media (Facebook, iMessage, Slack, etc.). Backed
                  by the /api/share/wishlist/:token endpoint which emits
                  Open Graph meta tags for crawlers. */}
              <div className="mt-4">
                <p className="text-xs font-medium mb-2 flex items-center gap-1" style={{ color: darkMode?.isDark ? '#E5E5E5' : '#73766A' }}>
                  <Eye className="w-3 h-3" /> Preview when shared
                </p>
                <div
                  data-testid="wishlist-share-preview-card"
                  className="rounded-lg overflow-hidden border"
                  style={{ borderColor: darkMode?.isDark ? 'rgba(255,255,255,0.1)' : '#E5E7EB' }}
                >
                  <div
                    className="h-24 sm:h-28 bg-cover bg-center"
                    style={{
                      backgroundImage: 'url(https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=1200&q=80)'
                    }}
                  />
                  <div className="p-3" style={{ background: darkMode?.isDark ? 'rgba(255,255,255,0.04)' : '#FFFFFF' }}>
                    <p className="text-[10px] uppercase tracking-wide" style={{ color: darkMode?.isDark ? '#E5E5E5' : '#73766A' }}>
                      mementa
                    </p>
                    <p className="text-sm font-semibold leading-tight mt-0.5 line-clamp-1" style={{ color: darkMode?.isDark ? '#FFFAF5' : '#20212B' }}>
                      {wishlist.name}, a wishlist on Mementa
                    </p>
                    <p className="text-xs leading-snug mt-1 line-clamp-2" style={{ color: darkMode?.isDark ? '#E5E5E5' : '#73766A' }}>
                      Help build this collaborative wishlist. Take a look and add your own suggestions!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
